import {
  actionToUci,
  canonicalToAbsolute,
  decodeAction,
  squareName,
  uciToAction,
} from "./action";
import { chess, isChecked, legalActions } from "./environment";
import { Piece, type ChessState } from "./types";

export interface ParsedPgn {
  readonly headers: Readonly<Record<string, string>>;
  readonly states: readonly ChessState[];
}

const PIECES: Readonly<Record<string, Piece>> = {
  K: Piece.King,
  Q: Piece.Queen,
  R: Piece.Rook,
  B: Piece.Bishop,
  N: Piece.Knight,
};

function stripMovetextNoise(text: string): string {
  let result = "";
  let variationDepth = 0;
  let braceDepth = 0;
  let lineComment = false;
  for (const character of text) {
    if (lineComment) {
      if (character === "\n" || character === "\r") lineComment = false;
      else continue;
    }
    if (braceDepth > 0) {
      if (character === "{") braceDepth += 1;
      if (character === "}") braceDepth -= 1;
      continue;
    }
    if (character === ";") {
      lineComment = true;
      continue;
    }
    if (character === "{") {
      braceDepth = 1;
      continue;
    }
    if (character === "(") {
      variationDepth += 1;
      continue;
    }
    if (character === ")") {
      if (variationDepth === 0) throw new RangeError("Invalid PGN: unmatched variation close");
      variationDepth -= 1;
      continue;
    }
    if (variationDepth === 0) result += character;
  }
  if (braceDepth > 0) throw new RangeError("Invalid PGN: unclosed comment");
  if (variationDepth > 0) throw new RangeError("Invalid PGN: unclosed variation");
  return result;
}

function parseHeaders(pgn: string): { headers: Record<string, string>; movetext: string } {
  const headers: Record<string, string> = {};
  const movetext = pgn.replace(/^\s*\[([^\s\]]+)\s+"((?:\\.|[^"\\])*)"\s*\]\s*/gm,
    (_match, name: string, value: string) => {
      headers[name] = value.replace(/\\(["\\])/g, "$1");
      return " ";
    });
  if (/\[[^\]]*\]/.test(movetext)) throw new RangeError("Invalid PGN tag pair");
  return { headers, movetext };
}

function promotionPiece(state: ChessState, action: number): string | null {
  const move = decodeAction(action);
  if (state.board[move.from] !== Piece.Pawn || move.from % 8 !== 6) return null;
  return move.underpromotion < 0 ? "Q" : ["R", "B", "N"][move.underpromotion]!;
}

const SAN_PIECES: Readonly<Partial<Record<number, string>>> = {
  [Piece.Knight]: "N",
  [Piece.Bishop]: "B",
  [Piece.Rook]: "R",
  [Piece.Queen]: "Q",
  [Piece.King]: "K",
};

/** Format one legal action as Standard Algebraic Notation. */
export function actionToSan(state: ChessState, action: number): string {
  const legal = legalActions(state);
  if (!legal.includes(action)) {
    throw new RangeError(`Cannot format illegal action as SAN: ${actionToUci(action, state.color)}`);
  }
  const move = decodeAction(action);
  const piece = state.board[move.from] ?? Piece.Empty;
  const from = squareName(canonicalToAbsolute(move.from, state.color));
  const to = squareName(canonicalToAbsolute(move.to, state.color));
  let san: string;

  if (piece === Piece.King && Math.abs(move.to - move.from) === 16) {
    san = to[0] === "g" ? "O-O" : "O-O-O";
  } else {
    const capture = (state.board[move.to] ?? 0) < 0 ||
      (piece === Piece.Pawn && move.to === state.enPassant);
    let disambiguation = "";
    if (piece === Piece.Pawn) {
      if (capture) disambiguation = from[0]!;
    } else {
      const competitors = legal.filter((candidate) => {
        if (candidate === action) return false;
        const decoded = decodeAction(candidate);
        return decoded.to === move.to && state.board[decoded.from] === piece;
      });
      if (competitors.length > 0) {
        const origins = competitors.map((candidate) => {
          const decoded = decodeAction(candidate);
          return squareName(canonicalToAbsolute(decoded.from, state.color));
        });
        if (origins.every((origin) => origin[0] !== from[0])) {
          disambiguation = from[0]!;
        } else if (origins.every((origin) => origin[1] !== from[1])) {
          disambiguation = from[1]!;
        } else {
          disambiguation = from;
        }
      }
    }
    const promotion = promotionPiece(state, action);
    san = `${SAN_PIECES[piece] ?? ""}${disambiguation}${capture ? "x" : ""}${to}${
      promotion ? `=${promotion}` : ""
    }`;
  }

  const next = chess.step(state, action);
  if (next.terminalReason === "checkmate") return `${san}#`;
  return isChecked(next) ? `${san}+` : san;
}

function resultToken(state: ChessState): string {
  if (!chess.isTerminal(state)) return "*";
  if (state.rewards[0] > state.rewards[1]) return "1-0";
  if (state.rewards[1] > state.rewards[0]) return "0-1";
  return "1/2-1/2";
}

/** Serialize a standard-start state timeline as copyable PGN movetext. */
export function toPgn(states: readonly ChessState[]): string {
  if (states.length === 0) throw new RangeError("Cannot serialize an empty game timeline");
  const tokens: string[] = [];
  for (let index = 1; index < states.length; index += 1) {
    const previous = states[index - 1]!;
    const current = states[index]!;
    const record = current.moves.at(-1);
    if (!record) throw new RangeError(`Missing move record at ply ${index}`);
    if (previous.color === 0) tokens.push(`${previous.fullmoveCount}.`);
    else if (index === 1) tokens.push(`${previous.fullmoveCount}...`);
    tokens.push(actionToSan(previous, record.action));
  }
  tokens.push(resultToken(states.at(-1)!));
  return tokens.join(" ");
}

function actionForSan(state: ChessState, originalToken: string): number {
  const token = originalToken
    .replace(/[+#?!]+$/g, "")
    .replace(/(?:e\.?p\.?)$/i, "")
    .replace(/^0-0-0$/, "O-O-O")
    .replace(/^0-0$/, "O-O");
  const legal = legalActions(state);
  if (/^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(token)) {
    const action = uciToAction(state, token);
    if (legal.includes(action)) return action;
    throw new RangeError(`Illegal PGN move: ${originalToken}`);
  }
  if (token === "O-O" || token === "O-O-O") {
    const destination = token === "O-O" ? "g" : "c";
    const match = legal.filter((action) => {
      const move = decodeAction(action);
      return state.board[move.from] === Piece.King &&
        squareName(canonicalToAbsolute(move.to, state.color))[0] === destination &&
        Math.abs(move.to - move.from) === 16;
    });
    if (match.length === 1) return match[0]!;
    throw new RangeError(`Illegal PGN move: ${originalToken}`);
  }

  const match = /^(?<piece>[KQRBN])?(?<file>[a-h])?(?<rank>[1-8])?(?<capture>x)?(?<to>[a-h][1-8])(?:=?(?<promotion>[QRBN]))?$/.exec(token);
  if (!match?.groups) throw new RangeError(`Unsupported PGN move: ${originalToken}`);
  const groups = match.groups;
  const piece = groups.piece?.toUpperCase();
  const expectedPiece = piece ? PIECES[piece]! : Piece.Pawn;
  const candidates = legal.filter((action) => {
    const move = decodeAction(action);
    if (state.board[move.from] !== expectedPiece) return false;
    const from = squareName(canonicalToAbsolute(move.from, state.color));
    const to = squareName(canonicalToAbsolute(move.to, state.color));
    if (to !== groups.to!.toLowerCase()) return false;
    if (groups.file && from[0] !== groups.file.toLowerCase()) return false;
    if (groups.rank && from[1] !== groups.rank) return false;
    const isCapture = (state.board[move.to] ?? 0) < 0 ||
      (expectedPiece === Piece.Pawn && move.to === state.enPassant);
    if (Boolean(groups.capture) !== isCapture) return false;
    const promotion = promotionPiece(state, action);
    return (groups.promotion?.toUpperCase() ?? null) === promotion;
  });
  if (candidates.length === 0) throw new RangeError(`Illegal PGN move: ${originalToken}`);
  if (candidates.length > 1) throw new RangeError(`Ambiguous PGN move: ${originalToken}`);
  return candidates[0]!;
}

/** Parse standard-start PGN and replay its main line through the chess environment. */
export function fromPgn(pgn: string): ParsedPgn {
  if (!pgn.trim()) throw new RangeError("PGN is empty");
  const { headers, movetext } = parseHeaders(pgn.replace(/^\uFEFF/, ""));
  if (headers.SetUp === "1" || headers.FEN) {
    throw new RangeError("PGN setup positions are not supported; import a game from the standard starting position");
  }
  const tokens = stripMovetextNoise(movetext).trim().split(/\s+/).filter(Boolean);
  const states: ChessState[] = [chess.init()];
  let result: string | undefined;
  for (let rawToken of tokens) {
    if (/^\$\d+$/.test(rawToken)) continue;
    rawToken = rawToken.replace(/^\d+\.(?:\.\.)?/, "");
    if (!rawToken || /^\.+$/.test(rawToken)) continue;
    if (/^(?:1-0|0-1|1\/2-1\/2|\*)$/.test(rawToken)) {
      result = rawToken;
      continue;
    }
    if (result) throw new RangeError("Invalid PGN: moves appear after the game result");
    const current = states[states.length - 1]!;
    if (chess.isTerminal(current)) {
      throw new RangeError(`PGN continues after the game ended at ply ${states.length - 1}`);
    }
    try {
      states.push(chess.step(current, actionForSan(current, rawToken)));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invalid move";
      throw new RangeError(`PGN ply ${states.length}: ${message}`);
    }
  }
  return { headers, states };
}
